import React, { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog, { dialogClasses } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '../../components/Button';
import { Loader } from '../../components';
import { formatDate } from '../../utils/formatTime';
import { PORTAL_ROUTE } from '../../constants/routes';
import { colors } from 'src/theme/palette/darkPalette';
import { DEVICES_PREVIOUS_PATH } from '../devices/deviceDetails/constant';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useExportDevices } from 'src/hooks/device';
import { useInfiniteDashboardEnforcementDevices } from 'src/hooks/dashboard';
import { DEVICE_STATUS } from 'src/utils/enums/deviceStatus';
import { HEALTH_STATUS } from 'src/utils/enums/healthStatus';
import { WDAC_STATUS } from 'src/utils/enums/wdacStatus';
import { objectToQueryString } from 'src/utils/uri';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${dialogClasses.paper}`]: {
    backgroundColor: '#4B4B4B',
    padding: theme.spacing(2),
    maxWidth: '700px',
    width: '100%',
    margin: theme.spacing(1),
  },
}));

const StyledDialogTitle = styled(DialogTitle)({
  fontSize: '20px',
  fontWeight: 'bold',
  textAlign: 'center',
  paddingBottom: '12px',
  paddingTop: '8px',
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(18),
  fontWeight: 'bold',
  color: '#FFA726',
  marginBottom: theme.spacing(1.5),
  textAlign: 'center',
}));

const IssueDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
  color: colors.steelBlue,
  fontWeight: 'bold',
  marginBottom: theme.spacing(1),
}));

const ProblemDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  color: '#fff',
  marginBottom: theme.spacing(1.5),
  lineHeight: 1.5,
}));

const SolutionText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
  color: colors.steelBlue,
  fontWeight: 'bold',
  marginBottom: theme.spacing(0.5),
}));

const SolutionSteps = styled('ol')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  color: '#fff',
  paddingLeft: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  '& li': {
    marginBottom: theme.spacing(0.5),
  },
}));

const DeviceItem = styled(Box)(({ theme }) => ({
  padding: '4px 8px',
  backgroundColor: '#3a3a3a',
  borderRadius: theme.spacing(1),
  border: '1px solid #FFA726',
  marginBottom: theme.spacing(0.5),
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#4a4a4a',
    borderColor: '#FFB74D',
  },
}));

const DeviceName = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 'bold',
  color: '#fff',
}));

const DeviceInfo = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  color: '#ccc',
}));

const AffectedDevicesTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
  color: colors.steelBlue,
  fontWeight: 'bold',
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1),
}));

const UnknownEnforcementModal = ({ open, onClose, tagId }) => {
  const navigate = useNavigate();
  const apiQuery = useMemo(
    () => ({
      ...(tagId ? { TagId: tagId } : {}),
      Statuses: [DEVICE_STATUS.Approved, DEVICE_STATUS.Unknown],
      HealthStatuses: [HEALTH_STATUS.Healthy],
      Status: WDAC_STATUS.Unknown,
    }),
    [tagId],
  );

  const devicesPageQuery = useMemo(
    () => ({
      ...(tagId ? { TagId: tagId } : {}),
      Statuses: [DEVICE_STATUS.Approved, DEVICE_STATUS.Unknown],
      HealthStatuses: [HEALTH_STATUS.Healthy],
      WdacStatuses: [WDAC_STATUS.Unknown],
    }),
    [tagId],
  );

  const devicesQuery = useInfiniteDashboardEnforcementDevices({
    apiQuery,
    limit: 48,
    enabled: open,
  });
  const exportDevicesMutation = useExportDevices();

  const devices = useMemo(
    () => devicesQuery.data?.pages?.flatMap(page => page.devices || []) || [],
    [devicesQuery.data],
  );

  const loadMore = useCallback(() => {
    if (devicesQuery.hasNextPage) {
      devicesQuery.fetchNextPage();
    }
  }, [devicesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: devicesQuery.isFetchingNextPage,
    hasNextPage: devicesQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const totalCount = devicesQuery.data?.pages?.[0]?.total || 0;

  const handleDeviceClick = device => {
    if (!device.id) {
      return;
    }

    const currentPath = window.location.pathname;
    sessionStorage.setItem(DEVICES_PREVIOUS_PATH, currentPath);
    navigate(`${PORTAL_ROUTE}/devices/${device.id}/general`);
    onClose();
  };

  const handleExport = useCallback(async () => {
    try {
      const data = await exportDevicesMutation.mutateAsync({ query: apiQuery });
      if (!data?.fileContents) {
        return;
      }

      const fileData = atob(data.fileContents);
      const byteArray = new Uint8Array(fileData.length);
      for (let index = 0; index < fileData.length; index += 1) {
        byteArray[index] = fileData.charCodeAt(index);
      }

      const blob = new Blob([byteArray], { type: data.contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.fileDownloadName || 'unknown-enforcement-devices.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_error) {}
  }, [apiQuery, exportDevicesMutation]);

  const handleOpenInDevices = useCallback(() => {
    const queryString = objectToQueryString(devicesPageQuery);
    navigate(`${PORTAL_ROUTE}/devices?${queryString}`);
    onClose();
  }, [devicesPageQuery, navigate, onClose]);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md">
      <StyledDialogTitle>Unknown Device Enforcement State</StyledDialogTitle>

      <DialogContent sx={{ maxHeight: '90vh', overflowY: 'auto', paddingX: 2, paddingTop: 1 }}>
        {devicesQuery.isLoading && (
          <Stack width="100%" justifyContent="center" alignItems="center" minHeight="200px">
            <Loader />
            <Typography sx={{ mt: 2, color: '#ccc' }}>Loading enforcement status...</Typography>
          </Stack>
        )}

        {!devicesQuery.isLoading && (
          <>
            <SectionTitle>Issue Detected</SectionTitle>

            <ProblemDescription>
              {totalCount} device{totalCount !== 1 ? 's are' : ' is'} showing as "Unknown"
              enforcement state. This means AppControl.ai cannot determine if these devices are
              running WDAC in Audit or Enforced mode.
            </ProblemDescription>

            <IssueDescription>Root Cause</IssueDescription>

            <ProblemDescription>
              The most likely cause is a missing deny rule for <strong>AppControlHeartbeat.dll</strong>
              in your WDAC policy. This rule is required for the heartbeat mechanism to work
              properly.
            </ProblemDescription>

            <SolutionText>Solution</SolutionText>

            <SolutionSteps>
              <li>Add a hash-based deny rule for AppControlHeartbeat.dll to your WDAC policy</li>
              <li>Deploy the updated policy to affected devices</li>
              <li>Wait for the next log collector run</li>
            </SolutionSteps>

            {devices.length > 0 && (
              <>
                <AffectedDevicesTitle>Affected Devices ({totalCount})</AffectedDevicesTitle>

                <Stack gap={0.5} sx={{ maxHeight: '272px', overflowY: 'auto' }}>
                  {devices.map((device, index) => (
                    <DeviceItem
                      key={device.id || index}
                      onClick={() => handleDeviceClick(device)}
                      title="Click to view device details"
                      ref={index === devices.length - 1 ? sentryRef : null}>
                      <DeviceName>{device.name || `DEVICE-${index + 1}`}</DeviceName>
                      {device.lastWdacHeartbeatDate && (
                        <DeviceInfo>
                          Last WDAC heartbeat: {formatDate(device.lastWdacHeartbeatDate)}
                        </DeviceInfo>
                      )}
                      {device.lastContactDate && (
                        <DeviceInfo>Last contact: {formatDate(device.lastContactDate)}</DeviceInfo>
                      )}
                      {device.osVersion && <DeviceInfo>OS: {device.osVersion}</DeviceInfo>}
                    </DeviceItem>
                  ))}
                  {devicesQuery.isFetchingNextPage && (
                    <Stack width="100%" justifyContent="center" alignItems="center" py={2}>
                      <Typography sx={{ mt: 1, color: '#ccc', fontSize: '12px' }}>
                        Loading more devices...
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </>
            )}
          </>
        )}
      </DialogContent>

      <StyledDialogActions>
        <Button
          onClick={handleExport}
          variant="outlined"
          disabled={exportDevicesMutation.isLoading || devicesQuery.isLoading || totalCount === 0}>
          {exportDevicesMutation.isLoading ? 'Exporting...' : 'Export'}
        </Button>
        <Button onClick={handleOpenInDevices} variant="outlined" disabled={totalCount === 0}>
          Open in Devices
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default memo(UnknownEnforcementModal);
